import React, { useState, useEffect } from "react";
import axios from "axios";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import { getToken, setToken } from "../../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Input, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";

const UpdateAuthPasswordMethodModal: React.FC = () => {
  // From your modal provider:
  const { isUpdateAuthPasswordModalVisible, closeUpdateAuthPasswordModal, showErrorMessage } =
    useModalState();
  // From your global state (for user info & refresh):
  const { info, refreshAccountInfo } = useGlobalState();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Always call useEffect, but conditionally do setup inside it
  useEffect(() => {
    // If modal not visible, just return
    if (!isUpdateAuthPasswordModalVisible) {
      return;
    }
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setValidationError("");
  }, [info?.email, isUpdateAuthPasswordModalVisible]);

  // Submit handler
  const handleSubmit = async () => {
    if (newPassword && newPassword !== confirmNewPassword) {
      setValidationError("New passwords do not match.");
      return;
    }
    setValidationError("");

    // Basic token/env checks
    const bearerToken = getToken();
    if (!bearerToken) {
      showErrorMessage(`No bearer token found. Please sign in first.`);
      return;
    }
    // Attempt to update email/password
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/update_password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
      if (process.env.NODE_ENV === 'development') console.log("Password updated successfully:", response.data);

      // set new bearer token
      const newToken = response.data?.newToken;
      if (newToken) {
        setToken(newToken);
      }

      // Refresh user info so the new email shows up
      await refreshAccountInfo();

      // Close modal on success
      closeUpdateAuthPasswordModal();
    } catch (err: any) {
      showErrorMessage(
        `An unexpected error occurred. ${err.response?.data?.message}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot lazyMount open={isUpdateAuthPasswordModalVisible} onOpenChange={(e) => { if (!e.open) closeUpdateAuthPasswordModal(); }}>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isLoading) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Change your password</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Field label="Current password">
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
            <Field label="New password">
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
            <Field label="Confirm new password">
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
            {validationError && (
              <Text fontSize="sm" color="#dc2626">
                {validationError}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={closeUpdateAuthPasswordModal}>
            Cancel
          </Button>
          <Button colorPalette="purple" bg="#9333ea" onClick={handleSubmit} loading={isLoading}>
            Update password
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default UpdateAuthPasswordMethodModal;
